import React, { useEffect, useId, useRef } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import classNames from "classnames";
import { Counterparty } from "@common/types/counterparty";
import { emptyToNull } from "@common/helpers/string";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { AddressForm } from "@components/forms/AddressForm";
import { VStack } from "@design-system/VStack/VStack";
import {
    Eligible1099Field,
    FederalTaxClassificationField,
    FormSectionCard,
    NoteField,
} from "./CounterpartyFormFields";
import { CounterpartyTinSection } from "./tin/CounterpartyTinSection";
import { useTinDraft } from "./tin/useTinDraft";
import {
    DEFAULT_COUNTRY,
    CounterpartyFormSubmission,
    FormSchema,
    buildCounterpartyFormInitialValues,
    counterpartyFormSchema,
    omitContactAndAddress,
    omitEmptyAddress,
    toAddressScalars,
    toCompanyCounterpartyUpdate,
} from "./counterpartyFormSchema";
import styles from "./CounterpartyForm.module.scss";

export type {
    CounterpartyFormSubmission,
    FormSchema,
} from "./counterpartyFormSchema";

const counterpartyFields = createFormikFields<FormSchema>();

export type CounterpartyFormFields = "full" | "company" | "extracted";

export interface CounterpartyFormSubmitHelpers {
    /** Saves a typed tax ID onto `counterparty`; resolves false when it failed. */
    saveTin: (counterparty: Counterparty) => Promise<boolean>;
    /** Saves a typed tax ID onto an already saved `counterparty`, toasting on failure. */
    saveTinOrNotify: (
        counterparty: Counterparty,
        savedVerb: "Created" | "Updated",
    ) => Promise<void>;
}

interface Props {
    counterparty?: Counterparty;
    initialValues?: Partial<FormSchema>;
    fullWidth?: boolean;
    onSubmit: (
        data: CounterpartyFormSubmission,
        helpers: CounterpartyFormSubmitHelpers,
    ) => Promise<void>;
    submitText: string;
    onCancelled: () => void;
    sectionClassName?: string;
    /** `company` hides person/address controls so those are edited as child rows. */
    fields?: CounterpartyFormFields;
    // Rendered between the Basic info and Tax sections (e.g. the Contacts and
    // Addresses book tables in the counterparty editor drawer/modal), keeping
    // field order consistent everywhere: Basic info, Contacts, Addresses,
    // Tax, Note.
    betweenBasicInfoAndTax?: React.ReactNode;
    dirtyRef?: React.MutableRefObject<boolean>;
    valuesRef?: React.MutableRefObject<FormSchema | null>;
    submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
    // Notifies the parent as submission starts/finishes so an out-of-form footer
    // (e.g. the modal footers in the counterparty editor drawers) can show save
    // progress and disable Cancel while the write is in flight.
    onSubmittingChange?: (isSubmitting: boolean) => void;
    formId?: string;
    footer?: "form" | "none";
}

export const CounterpartyForm: React.FC<Props> = ({
    counterparty,
    initialValues,
    fullWidth,
    onSubmit,
    onCancelled,
    submitText,
    sectionClassName,
    fields = "full",
    betweenBasicInfoAndTax,
    dirtyRef,
    valuesRef,
    submitRef,
    onSubmittingChange,
    formId,
    footer = "form",
}) => {
    const noteHeadingId = useId();
    const generatedFormId = useId();
    const resolvedFormId = formId ?? generatedFormId;
    const { error: showErrorToast } = useToaster();
    const tinDraft = useTinDraft();
    const submitHelpers: CounterpartyFormSubmitHelpers = {
        saveTin: tinDraft.save,
        saveTinOrNotify: async (savedCounterparty, savedVerb) => {
            const isTinSaved = await tinDraft.save(savedCounterparty);
            if (!isTinSaved) {
                showErrorToast(
                    `${savedVerb} counterparty, but couldn't save the tax ID`,
                );
            }
        },
    };
    const saveCounterparty = ({
        name,
        companyName,
        city,
        email,
        cc,
        bcc,
        phoneNumber,
        website,
        title,
        firstName,
        middleName,
        lastName,
        suffix,
        postalCode,
        street,
        street2,
        country: countryInput,
        state: stateInput,
        isEligible1099,
        federalTaxClassification,
        note,
    }: FormSchema) => {
        const state = countryInput !== "US" ? null : stateInput;
        const addressEmpty =
            !state && !city && !postalCode && !street && !street2;
        const country =
            countryInput === DEFAULT_COUNTRY && addressEmpty
                ? null
                : countryInput;

        const normalized: FormSchema = {
            name,
            companyName: emptyToNull(companyName),
            email: email ?? null,
            cc: emptyToNull(cc),
            bcc: emptyToNull(bcc),
            phoneNumber: phoneNumber ?? null,
            website: website ?? null,
            title: emptyToNull(title),
            firstName: emptyToNull(firstName),
            middleName: emptyToNull(middleName),
            lastName: emptyToNull(lastName),
            suffix: emptyToNull(suffix),
            street: street ?? null,
            street2: street2 ?? null,
            city: city ?? null,
            postalCode: postalCode ?? null,
            state: state ?? null,
            country: country ?? null,
            isEligible1099,
            federalTaxClassification,
            note: emptyToNull(note),
        };

        if (fields === "extracted") {
            return onSubmit(omitContactAndAddress(normalized), submitHelpers);
        }

        if (fields === "company") {
            // Any address field in the payload makes the backend rewrite
            // the primary address (seeding a country-only row, or deleting
            // a reused local copy's address), so omit an empty address.
            return onSubmit(
                omitEmptyAddress({
                    ...buildCounterpartyFormInitialValues({
                        counterparty,
                        initialValues,
                    }),
                    ...toCompanyCounterpartyUpdate(normalized),
                    ...toAddressScalars(normalized),
                }),
                submitHelpers,
            );
        }

        return onSubmit(normalized, submitHelpers);
    };

    const latestValuesRef = useRef<FormSchema | null>(null);
    const formik = useFormik<FormSchema>({
        initialValues: buildCounterpartyFormInitialValues({
            counterparty,
            initialValues,
        }),
        validate: validateFormikWithZodSchema(counterpartyFormSchema),
        onSubmit: submitHelper({
            // The submitted values become the new baseline so the drawer (which
            // stays open after an edit) doesn't prompt to discard saved changes.
            // Edits typed while the save was in flight are restored on top, so
            // they stay dirty instead of being overwritten or marked as saved.
            handler: async (submittedValues, { resetForm, setValues }) => {
                if (!tinDraft.validate()) {
                    return;
                }
                await saveCounterparty(submittedValues);
                const latestValues = latestValuesRef.current ?? submittedValues;
                resetForm({ values: submittedValues });
                await setValues(latestValues);
            },
        }),
    });
    latestValuesRef.current = formik.values;

    if (dirtyRef) {
        dirtyRef.current = formik.dirty || tinDraft.hasInput;
    }
    if (valuesRef) {
        valuesRef.current = formik.values;
    }
    if (submitRef) {
        submitRef.current = () => formik.submitForm();
    }

    useEffect(() => {
        onSubmittingChange?.(formik.isSubmitting);
    }, [formik.isSubmitting, onSubmittingChange]);

    const isExtracted = fields === "extracted";
    const isCompanyLike = fields === "company" || isExtracted;
    const textFieldClassName = (name: "name" | "companyName" | "website") => {
        const suggestedValue = initialValues?.[name];
        return classNames(styles.textField, {
            [styles.suggested]:
                isExtracted &&
                !counterparty &&
                Boolean(suggestedValue) &&
                formik.values[name] === suggestedValue,
        });
    };

    return (
        <FormikProvider value={formik}>
            <Form id={resolvedFormId}>
                <FormSectionCard
                    title="Basic info"
                    fullWidth={fullWidth}
                    sectionClassName={sectionClassName}
                >
                    <counterpartyFields.Text
                        name="name"
                        label="Display name"
                        size="lg"
                        showErrorMessage={false}
                        className={textFieldClassName("name")}
                    />
                    <counterpartyFields.Text
                        name="companyName"
                        label="Company"
                        size="lg"
                        showErrorMessage={false}
                        className={textFieldClassName("companyName")}
                    />
                    {!isCompanyLike && (
                        <counterpartyFields.Text
                            name="email"
                            label="Email"
                            size="lg"
                            showErrorMessage={false}
                            className={styles.textField}
                        />
                    )}
                    {!isExtracted && (
                        <>
                            <counterpartyFields.Text
                                name="cc"
                                label="CC"
                                size="lg"
                                showErrorMessage={false}
                                className={styles.textField}
                            />
                            <counterpartyFields.Text
                                name="bcc"
                                label="BCC"
                                size="lg"
                                showErrorMessage={false}
                                className={styles.textField}
                            />
                        </>
                    )}
                    {!isCompanyLike && (
                        <counterpartyFields.Phone
                            name="phoneNumber"
                            label="Phone number"
                            size="xs"
                            className={styles.textField}
                        />
                    )}
                    <counterpartyFields.Text
                        name="website"
                        label="Website"
                        size="lg"
                        showErrorMessage={false}
                        className={textFieldClassName("website")}
                    />
                </FormSectionCard>

                {betweenBasicInfoAndTax}

                {!isCompanyLike && (
                    <FormSectionCard
                        title="Contact name"
                        fullWidth={fullWidth}
                        sectionClassName={sectionClassName}
                    >
                        <counterpartyFields.Text
                            name="title"
                            label="Title"
                            size="lg"
                            showErrorMessage={false}
                            className={styles.textField}
                        />
                        <counterpartyFields.Text
                            name="firstName"
                            label="First name"
                            size="lg"
                            showErrorMessage={false}
                            className={styles.textField}
                        />
                        <counterpartyFields.Text
                            name="middleName"
                            label="Middle name"
                            size="lg"
                            showErrorMessage={false}
                            className={styles.textField}
                        />
                        <counterpartyFields.Text
                            name="lastName"
                            label="Last name"
                            size="lg"
                            showErrorMessage={false}
                            className={styles.textField}
                        />
                        <counterpartyFields.Text
                            name="suffix"
                            label="Suffix"
                            size="lg"
                            showErrorMessage={false}
                            className={styles.textField}
                        />
                    </FormSectionCard>
                )}

                {!isCompanyLike && (
                    <FormSectionCard
                        title="Address"
                        fullWidth={fullWidth}
                        sectionClassName={sectionClassName}
                    >
                        <AddressForm
                            includeCountry
                            includeStreet2
                            prefix=""
                            fieldSize="xs"
                        />
                    </FormSectionCard>
                )}
            </Form>

            {/* The tax ID section renders its own VGS <form>, which can't nest
                inside the counterparty form, so the rest of the fields stay in
                Formik context and submit through the form attribute. The tax
                ID itself is saved by onSubmit through saveTin. */}
            <FormSectionCard
                title="Tax"
                fullWidth={fullWidth}
                sectionClassName={sectionClassName}
            >
                <VStack gap={16} className={styles.taxFields}>
                    <FederalTaxClassificationField />
                    <CounterpartyTinSection
                        counterparty={counterparty}
                        federalTaxClassification={
                            formik.values.federalTaxClassification
                        }
                        draft={tinDraft}
                        isSubmitting={formik.isSubmitting}
                        onSubmitRequest={() => void formik.submitForm()}
                    />
                    <Eligible1099Field />
                </VStack>
            </FormSectionCard>

            <FormSectionCard
                title="Note"
                titleId={noteHeadingId}
                fullWidth={fullWidth}
                sectionClassName={sectionClassName}
            >
                <NoteField aria-labelledby={noteHeadingId} />
            </FormSectionCard>

            {footer === "form" && (
                <footer
                    className={classNames(styles.footer, {
                        [styles.narrowFooter]: !fullWidth,
                    })}
                >
                    <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        onClick={onCancelled}
                        disabled={formik.isSubmitting}
                    >
                        Cancel
                    </Button>
                    <ButtonWithLoader
                        type="submit"
                        form={resolvedFormId}
                        variant="primary"
                        size="md"
                        loading={formik.isSubmitting}
                        hotKey="mod+enter"
                        hotKeyDisplayPosition="inline"
                        hotKeyOptions={{
                            description: "Save counterparty",
                            preventDefault: true,
                            enableOnFormTags: true,
                        }}
                    >
                        {submitText}
                    </ButtonWithLoader>
                </footer>
            )}
        </FormikProvider>
    );
};
